import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, computedFn, Fragment, autorun} from '@api'
import {Tooltip} from '@material-ui/core'
import {BuilderCardStore, CatalogFieldValueContextMenuItem, DEBUG_ASSORTMENTS, HeroCardsSecondLevelStore, ICatalogItemDetailsPathElement, IContextMenuItems, IContextualHoverItemStore, useHeroCardsSecondLevelStore, useVipLocalStore, VipLocalStore} from '@modules'
import {AssortmentCardAttributeConfig, ImageRecord, StoresInstance, ui} from '@stores'
import {MyImage} from 'centric-ui-components'
import {runInAction} from 'mobx'
import * as css from './HeroCardSecondLevel.css'

/// region Store

export class HeroCardSecondLevelStore extends VipLocalStore implements IContextMenuItems, IContextualHoverItemStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { secondLevelStore: HeroCardsSecondLevelStore }) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        if (this.isSelected) {
          runInAction(() => {
            this.selectThisItem()
          })
        }
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  get item() { return this.props.item}

  get heroCard() { return this.props.secondLevelStore.heroCard}

  get config() { return this.heroCard.config}

  get heroData() { return this.heroCard.data}

  @computed get attributes() {
    const {config: {child: {attributes}}, item, stores} = this

    return attributes.map(attr => _.isString(attr) ? _.get(item, attr) : attr(item, stores))
  }

  @computed get image() : ImageRecord {
    return _.first(this.images)
  }

  @computed get imageUrl() {
    const {catalog, images, image, config: {child}} = this,
      autoTransparency = _.isString(child.images) || !child.images.transparency ? false : true

    return catalog.imageUrlFor(image, {autoTransparency, imageType: 'png', c8Size: 'image'})
  }

  @computed get images() {
    const {item, config: {child}} = this
    if (_.isString(child.images)) {
      return _.get(item, child.images)
    } else {
      const {images: {field, transparency}} = child
      return _.get(item, field)
    }

    return _.get(item, 'images')
  }

  @computed get isSelected() {
    const {catalog, heroCard: {selectedSecondLevelItem, selectedHeroItem}, matchHeroItem} = this
    return selectedSecondLevelItem == this || (!selectedSecondLevelItem && matchHeroItem(selectedHeroItem))
  }

  matchHeroItem = (item) => {
    const {catalog, id} = this
    return catalog.idFor(item?.__parent, false) === id || catalog.idFor(item, false) === id
  }

  @action selectThisItem = () => {
    const {heroCard, heroCard: {imagesForHeroItem, filteredHeroItems, selectedHeroIndex}, matchHeroItem} = this,
      matchingHeroItems = filteredHeroItems.filter(item => matchHeroItem(item)),
      firstMatchingIndex = filteredHeroItems.indexOf(_.first(matchingHeroItems))

    let index = selectedHeroIndex ?? firstMatchingIndex

    // Try to find a further item that matches but has images
    const itemsWithImage = matchingHeroItems.filter(item => !_.isEmpty(imagesForHeroItem(item)))
    index = _.isEmpty(itemsWithImage) ? firstMatchingIndex : filteredHeroItems.indexOf(itemsWithImage[0])

    
    heroCard.selectedSecondLevelItem = this
    if (index != -1) {
      heroCard.selectedHeroIndex = index
    } else {
      heroCard.selectedHeroIndex = null
      // console.debug(`Unable to find hero card for`, {id, filteredHeroItems})
    }
  }

  @action onClick = () => {
    if (!this.isSelected) {
      this.selectThisItem()
    }
    else {
      this.heroCard.selectedSecondLevelItem = null
      this.heroCard.autoSelectHeroItem()
    }
  }

  @computed get catalogField() {
    const {catalog: {_fieldByName}, config: {child}} = this
    if (!child) return undefined
    if (!child.field) return null
    return _fieldByName[child.field]
  }

  @computed get foreignKeyField() {
    const {catalog, catalogField} = this
    return catalogField === null ? null : catalogField ? catalog.findFieldInTree(catalogField.field, true) : undefined
  }

  @computed get fieldValueContextMenuItem() {
    const {foreignKeyField, item} = this
    return foreignKeyField !== undefined && <CatalogFieldValueContextMenuItem field={foreignKeyField} value={item}/>
  }

  get contextMenuItems() {
    return <HeroCardSecondLevelStoreContext.Provider value={this}>
      {this.fieldValueContextMenuItem}
      {this.heroCard.contextMenuItems}
    </HeroCardSecondLevelStoreContext.Provider>
  }

  @action onContextMenu = e => this.contextMenu.onComponentContextMenu(e, this, {item: this.item})

  @computed get detailsPath(): ICatalogItemDetailsPathElement[] {
    const {foreignKeyField, catalog: {idFor}, catalog, item} = this
    if (!item || foreignKeyField === undefined) return []

    return [
      {
        where: [{value: idFor(item, false)}],
        esIndex: foreignKeyField?.esIndex ?? catalog?.index,
        catalogField: foreignKeyField?.field
      }
    ]
  }

  @computed get associatedWorkspace() {
    const {catalog, foreignKeyField} = this
    return foreignKeyField ? catalog.workspaceWrapperForField(foreignKeyField) : catalog.workspace
  }

  @action onDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    const {detailsPath, associatedWorkspace, stores: {flexLayoutCatalogRoot: {tab}}} = this

    !_.isEmpty(detailsPath) && associatedWorkspace && tab.openCatalogItemDetailsInNewTab({
      path: detailsPath,
      workspace: associatedWorkspace._id,
      parentTab: tab.id
    })

    this.assortment.scrollIntoView()
  }

  get assortment() { return this.heroCard.cardStore.card}

  @computed get title() {
    const {associatedWorkspace, item, strings} = this

    if (!item || !associatedWorkspace) return strings.emptyLabel

    return `${_.get(item, associatedWorkspace.dataMap.title) ?? strings.emptyLabel}` //\n(${associatedWorkspace.catalog.catalogItemName ?? associatedWorkspace.title})`
  }

  @computed get id() {
    return this.catalog.idFor(this.item)
  }

  @computed get hoverId() {
    return `${this.associatedWorkspace?._id}-${this.id}`
  }

  matchesHoverItem = computedFn((card: BuilderCardStore): boolean => {
    const {catalogField, id} = this, {props: {nested}, data} = card

    // console.log({item, id, nested, data, catalogField})

    if (!catalogField && !nested) {
      return card.id === id
    }

    return false
  })

  @computed get mouseEvents() {
    return this.assortment.hoverItemEventsFor(this)
  }

  matchesHeroItem = (heroItem: any) => {
    const {catalog: {idFor}, id, config: {item: configItem, field}, item, props: {secondLevelStore}} = this

    return idFor(heroItem?.__parent) == id || idFor(heroItem, false) === id
  }

  @action onCopyStoreToWindow = () => {
    window['assortmentSecondLevel'] = this
    DEBUG_ASSORTMENTS && console.debug(`Copied store for '${this.title}' into window.assortmentSecondLevel and clipboard`)
    navigator.clipboard.writeText(`window.assortmentSecondLevel`)
  }

  @computed get hide() {
    const {heroCard: {hasBOMItems}, config: {child}, stores, item} = this

    if (hasBOMItems && child?.bom?.hideParent) {
      return child.bom.hideParent(item, stores)
    }

    return false
  }
}

export const HeroCardSecondLevelStoreContext = React.createContext<HeroCardSecondLevelStore>(null)
export const useHeroCardSecondLevelStore = () => React.useContext(HeroCardSecondLevelStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  item?: any
  index: number
}

export const HeroCardSecondLevel = observer<MyProps>(function HeroCardSecondLevel({item, index, className}) {
  const secondLevelStore = useHeroCardsSecondLevelStore(),
    obsProps = useAsObservableSource({item, index, secondLevelStore}),
    store = useVipLocalStore((stores) => new HeroCardSecondLevelStore(stores, obsProps)),
    {imageUrl, hide, image, isSelected, onClick, onContextMenu, id, onDoubleClick, title, config: {child: {attributes}}, mouseEvents} = store

  useEffect(() => store.registerEffects(), [])

  // console.log({item, index, bom})

  return (
    <HeroCardSecondLevelStoreContext.Provider value={store}>
      <Tooltip
        placement='bottom'
        title={title}
      >
        <div
          className={classNames(className, css.root)}
          data-selected={isSelected ? true : undefined}
          data-index={index}
          data-hide={hide ? true : undefined}
          data-id={id}
          onClick={onClick}
          {...mouseEvents}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        >
          <MyImage className={css.secondLevelImage}
                   imageUrl={imageUrl}
                   imageId={image?.exId}
                   lazy={false}
                   noImageContent={<Fragment/>}
          />
          {!_.isEmpty(attributes) && <div className={css.attributes}>
            {attributes.map((attr, i) => <HeroCardSecondLevelAttribute key={i} attribute={attr}/>)}

            {false && <div className={css.focusRectangle}/>}
		  </div>}
        </div>
      </Tooltip>
    </HeroCardSecondLevelStoreContext.Provider>)
})

export const HeroCardSecondLevelAttribute = observer<{ attribute: AssortmentCardAttributeConfig }>(function HeroCardSecondLevelAttribute({attribute}) {
  const {item, stores, strings, config: {child: {removeDecimalsInAttributes}}} = useHeroCardSecondLevelStore()
  let v = _.isString(attribute) ? _.get(item, attribute) : attribute(item, stores)

  if (_.isString(v) && removeDecimalsInAttributes) {
    v = v.replaceAll(/\.[0-9]+/g, '')
  }

  // console.log(item)
  return (<div className={css.attribute}
               data-field={attribute}>
    <span className={css.attributeValue}>{v /* || strings.emptyLabel*/}</span>
  </div>)
})
/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/HeroCards/HeroCardSecondLevel.tsx